import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createLandlord,
  deleteLandlord,
  getLandlords,
  updateLandlord,
} from "api/landlords";

import { Landlord } from "js/components/types";

import { queryKeys } from "./queryKeys";

interface PaginationParams {
  current?: number;
  pageSize?: number;
}

// Custom hook for fetching landlords
export const useLandlords = (params: PaginationParams = {}) => {
  return useQuery({
    queryKey: queryKeys.landlords.list(params),
    queryFn: () => getLandlords(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Custom hook for creating a landlord
export const useCreateLandlord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLandlord,
    onSuccess: () => {
      // Invalidate and refetch landlords list
      queryClient.invalidateQueries({
        queryKey: queryKeys.landlords.lists(),
      });
      // Invalidate properties as landlords may appear in property creation
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.all,
      });
    },
  });
};

// Custom hook for updating a landlord
export const useUpdateLandlord = ({
  onSuccess: onSuccessCallback,
  onError: onErrorCallback,
}: {
  onSuccess?: (data: any, variables: any) => void;
  onError?: (error: any) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      landlord,
      landlordId,
      queryParams,
    }: {
      landlord: Landlord;
      landlordId: string;
      queryParams?: object;
    }) => updateLandlord(landlord, landlordId, queryParams),
    onSuccess: (data, variables) => {
      // Invalidate and refetch landlords list
      queryClient.invalidateQueries({
        queryKey: queryKeys.landlords.lists(),
      });
      // Invalidate specific landlord detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.landlords.detail(variables.landlordId),
      });
      // Invalidate properties as they may be associated
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.all,
      });
      // Invalidate tenant reports as they may reference landlords
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.all,
      });
      // Invalidate contracts as they may reference landlords
      queryClient.invalidateQueries({
        queryKey: queryKeys.contracts.lists(),
      });

      if (onSuccessCallback) {
        onSuccessCallback(data, variables);
      }
    },
    onError: (error) => {
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    },
  });
};

// Custom hook for deleting a landlord
export const useDeleteLandlord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLandlord,
    onSuccess: (data, landlordId) => {
      // Invalidate and refetch landlords list
      queryClient.invalidateQueries({
        queryKey: queryKeys.landlords.lists(),
      });
      // Invalidate specific landlord detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.landlords.detail(landlordId),
      });
      // Invalidate properties as they may need to update
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.all,
      });
    },
  });
};
